import {
  type Graph,
  type GraphEdge,
  type GraphNode,
  singleNodeOfType,
  withEdges,
  withoutEdges,
} from '../models/graph';
import { type Level, oneWayEdgeIds } from '../models/level';
import { dijkstra } from '../algorithms/shortestPath';
import { edmondsKarp } from '../algorithms/maxFlow';
import { maxBipartiteMatching } from '../algorithms/matching';

export class GameEngine {
  readonly level: Level;
  private readonly oneWay: Set<string>;

  constructor(level: Level) {
    this.level = level;
    this.oneWay = oneWayEdgeIds(level.graph);
  }

  private get graph(): Graph {
    return this.level.graph;
  }

  get sourceNode(): GraphNode | undefined {
    return singleNodeOfType(this.graph, 'source');
  }

  get sinkNode(): GraphNode | undefined {
    return singleNodeOfType(this.graph, 'sink');
  }

  private resolveEdges(edgeIds: ReadonlySet<string>): GraphEdge[] {
    return this.graph.edges.filter((e) => edgeIds.has(e.id));
  }

  private buildAdjacency(edges: readonly GraphEdge[]): Map<string, string[]> {
    const adj = new Map<string, string[]>();
    const push = (from: string, to: string): void => {
      const list = adj.get(from);
      if (list) list.push(to);
      else adj.set(from, [to]);
    };

    for (const edge of edges) {
      push(edge.sourceId, edge.targetId);
      if (!this.graph.directed && !this.oneWay.has(edge.id)) {
        push(edge.targetId, edge.sourceId);
      }
    }
    return adj;
  }

  private touchedNodes(edges: readonly GraphEdge[]): Set<string> {
    const nodes = new Set<string>();
    for (const edge of edges) {
      nodes.add(edge.sourceId);
      nodes.add(edge.targetId);
    }
    return nodes;
  }

  private isForest(edges: readonly GraphEdge[]): boolean {
    const parent = new Map<string, string>();

    const find = (x: string): string => {
      if (!parent.has(x)) parent.set(x, x);
      if (parent.get(x) !== x) parent.set(x, find(parent.get(x)!));
      return parent.get(x)!;
    };

    for (const edge of edges) {
      const ra = find(edge.sourceId);
      const rb = find(edge.targetId);
      if (ra === rb) return false;
      parent.set(ra, rb);
    }
    return true;
  }

  private reachable(start: string, adj: Map<string, string[]>): Set<string> {
    const visited = new Set<string>([start]);
    const queue: string[] = [start];
    while (queue.length > 0) {
      const current = queue.shift()!;
      for (const neighbor of adj.get(current) ?? []) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }
    return visited;
  }

  private isPathDirectionValid(edges: readonly GraphEdge[]): boolean {
    if (edges.length === 0) return true;
    if (!edges.some((e) => this.oneWay.has(e.id))) return true;

    const src = this.sourceNode;
    if (!src) return true;

    const dirAdj = new Map<string, string[]>();
    const push = (from: string, to: string): void => {
      const list = dirAdj.get(from);
      if (list) list.push(to);
      else dirAdj.set(from, [to]);
    };
    for (const edge of edges) {
      push(edge.sourceId, edge.targetId);
      if (!this.oneWay.has(edge.id)) push(edge.targetId, edge.sourceId);
    }

    const visited = this.reachable(src.id, dirAdj);
    for (const id of this.touchedNodes(edges)) {
      if (!visited.has(id)) return false;
    }
    return true;
  }

  private isSimplePathOrSubpath(edges: readonly GraphEdge[]): boolean {
    if (edges.length === 0) return true;
    if (!this.isForest(edges)) return false;

    const degree = new Map<string, number>();
    for (const edge of edges) {
      degree.set(edge.sourceId, (degree.get(edge.sourceId) ?? 0) + 1);
      degree.set(edge.targetId, (degree.get(edge.targetId) ?? 0) + 1);
    }
    return [...degree.values()].every((d) => d <= 2);
  }

  private isMatching(edges: readonly GraphEdge[]): boolean {
    const usedNodes = new Set<string>();
    for (const edge of edges) {
      if (usedNodes.has(edge.sourceId)) return false;
      usedNodes.add(edge.sourceId);
      if (usedNodes.has(edge.targetId)) return false;
      usedNodes.add(edge.targetId);
    }
    return true;
  }

  private degreeMap(edges: readonly GraphEdge[]): Map<string, number> {
    const degree = new Map<string, number>();
    for (const edge of edges) {
      degree.set(edge.sourceId, (degree.get(edge.sourceId) ?? 0) + 1);
      degree.set(edge.targetId, (degree.get(edge.targetId) ?? 0) + 1);
    }
    return degree;
  }

  validateMove(selectedEdgeIds: ReadonlySet<string>): boolean {
    if (selectedEdgeIds.size === 0) return true;

    const edges = this.resolveEdges(selectedEdgeIds);
    if (edges.length !== selectedEdgeIds.size) return false;

    switch (this.level.type) {
      case 'shortestPathPlanar':
      case 'shortestPath':
        if (!this.isSimplePathOrSubpath(edges)) return false;
        return this.isPathDirectionValid(edges);

      case 'mst':
        return this.isForest(edges);

      case 'maxFlow':
        return true;

      case 'minCut':
        return true;

      case 'matching':
        return this.isMatching(edges);

      case 'interdiction':
        return selectedEdgeIds.size <= this.level.budget;

      case 'tsp':
        return [...this.degreeMap(edges).values()].every((d) => d <= 2);
    }
  }

  calculateFlowScore(edgeFlows: ReadonlyMap<string, number>): number {
    const src = this.sourceNode;
    if (!src) return 0;

    let total = 0;
    for (const edge of this.graph.edges) {
      if (edge.sourceId === src.id) total += edgeFlows.get(edge.id) ?? 0;
    }
    return total;
  }

  getFlowImbalances(edgeFlows: ReadonlyMap<string, number>): Map<string, number> {
    const srcId = this.sourceNode?.id;
    const snkId = this.sinkNode?.id;
    const result = new Map<string, number>();

    for (const node of this.graph.nodes) {
      if (node.id === srcId || node.id === snkId) continue;

      let inFlow = 0;
      let outFlow = 0;
      for (const edge of this.graph.edges) {
        const flow = edgeFlows.get(edge.id) ?? 0;
        if (edge.targetId === node.id) inFlow += flow;
        if (edge.sourceId === node.id) outFlow += flow;
      }
      result.set(node.id, inFlow - outFlow);
    }
    return result;
  }

  isFlowSolutionComplete(edgeFlows: ReadonlyMap<string, number>): boolean {
    if (edgeFlows.size === 0) return false;

    let hasNonZero = false;
    for (const edge of this.graph.edges) {
      const flow = edgeFlows.get(edge.id) ?? 0;
      if (flow < 0 || flow > Math.trunc(edge.capacity)) return false;
      if (flow > 0) hasNonZero = true;
    }
    if (!hasNonZero) return false;

    return [...this.getFlowImbalances(edgeFlows).values()].every((v) => v === 0);
  }

  calculateScore(
    selectedEdgeIds: ReadonlySet<string>,
    edgeFlows?: ReadonlyMap<string, number>,
  ): number {
    if (selectedEdgeIds.size === 0) return 0;

    const edges = this.resolveEdges(selectedEdgeIds);
    const sumWeight = (): number => edges.reduce((sum, e) => sum + e.weight, 0);

    switch (this.level.type) {
      case 'shortestPathPlanar':
      case 'shortestPath':
      case 'mst':
      case 'tsp':
        return sumWeight();

      case 'maxFlow': {
        if (edgeFlows && edgeFlows.size > 0) return this.calculateFlowScore(edgeFlows);
        const src = this.sourceNode;
        const snk = this.sinkNode;
        if (!src || !snk) return 0;
        return edmondsKarp(withEdges(this.graph, edges), src.id, snk.id);
      }

      case 'minCut':
        return edges.reduce((sum, e) => sum + e.capacity, 0);

      case 'matching':
        return edges.length;

      case 'interdiction': {
        const src = this.sourceNode;
        const snk = this.sinkNode;
        if (!src || !snk) return 0;

        const reduced = withoutEdges(this.graph, selectedEdgeIds);
        const path = dijkstra(reduced, src.id, snk.id);
        if (path.length === 0 && src.id !== snk.id) return Infinity;
        return path.reduce(
          (sum, edgeId) => sum + (reduced.edges.find((e) => e.id === edgeId)?.weight ?? 0),
          0,
        );
      }
    }
  }

  isSolutionComplete(
    selectedEdgeIds: ReadonlySet<string>,
    edgeFlows?: ReadonlyMap<string, number>,
  ): boolean {
    if (selectedEdgeIds.size === 0) return false;

    const edges = this.resolveEdges(selectedEdgeIds);
    if (edges.length !== selectedEdgeIds.size) return false;

    switch (this.level.type) {
      case 'shortestPathPlanar':
      case 'shortestPath': {
        const src = this.sourceNode;
        const snk = this.sinkNode;
        if (!src || !snk) return false;
        if (!this.isSimplePathOrSubpath(edges)) return false;
        return this.reachable(src.id, this.buildAdjacency(edges)).has(snk.id);
      }

      case 'mst': {
        const nodeCount = this.graph.nodes.length;
        if (edges.length !== nodeCount - 1) return false;
        if (!this.isForest(edges)) return false;

        const touched = this.touchedNodes(edges);
        if (touched.size !== nodeCount) return false;

        const first = [...touched][0]!;
        return this.reachable(first, this.buildAdjacency(edges)).size === nodeCount;
      }

      case 'maxFlow': {
        if (edgeFlows && edgeFlows.size > 0) return this.isFlowSolutionComplete(edgeFlows);
        const src = this.sourceNode;
        const snk = this.sinkNode;
        if (!src || !snk) return false;

        const touched = this.touchedNodes(edges);
        if (!touched.has(src.id) || !touched.has(snk.id)) return false;
        return this.reachable(src.id, this.buildAdjacency(edges)).has(snk.id);
      }

      case 'minCut': {
        const src = this.sourceNode;
        const snk = this.sinkNode;
        if (!src || !snk) return false;

        const remaining = withoutEdges(this.graph, selectedEdgeIds);
        const adj = new Map<string, string[]>();
        const push = (from: string, to: string): void => {
          const list = adj.get(from);
          if (list) list.push(to);
          else adj.set(from, [to]);
        };
        for (const edge of remaining.edges) {
          push(edge.sourceId, edge.targetId);
          if (!this.graph.directed) push(edge.targetId, edge.sourceId);
        }
        return !this.reachable(src.id, adj).has(snk.id);
      }

      case 'matching': {
        if (!this.isMatching(edges)) return false;

        const leftIds = this.graph.nodes.filter((n) => n.type === 'source').map((n) => n.id);
        const rightIds = this.graph.nodes.filter((n) => n.type === 'sink').map((n) => n.id);

        if (leftIds.length > 0 && rightIds.length > 0) {
          const maxMatching = maxBipartiteMatching(this.graph, leftIds, rightIds);
          return edges.length >= maxMatching.size;
        }
        return edges.length > 0;
      }

      case 'interdiction':
        return selectedEdgeIds.size <= this.level.budget && selectedEdgeIds.size > 0;

      case 'tsp': {
        const nodeCount = this.graph.nodes.length;
        if (edges.length !== nodeCount) return false;

        const degree = this.degreeMap(edges);
        if (!this.graph.nodes.every((n) => (degree.get(n.id) ?? 0) === 2)) return false;

        const firstNodeId = this.graph.nodes[0]!.id;
        return this.reachable(firstNodeId, this.buildAdjacency(edges)).size === nodeCount;
      }
    }
  }
}
